/**
 * The Symbol column's search — `app` finds AAPL by ticker and Apple Inc. by name.
 *
 * ## Transient, and kept out of every filter that can be saved
 *
 * The search is **view state (D8)**: never written into `ScreenerConfig`, never
 * restored on reload, and cleared when the screen or the universe changes. It is
 * also kept out of AG Grid's own filter model, and that is the rule most likely
 * to be broken by a well-meant refactor. The column funnels are the *ad-hoc
 * layer* — `handleFilterChanged` reads `getFilterModel()` into `onAdHocChange`,
 * and the filter bar offers to merge that layer into the saved filter AST. A
 * "symbol or name contains" test has no equivalent in that AST, so a search that
 * lived in the model would either be dropped on the merge or turned into a
 * narrower filter than the one the user saw working.
 *
 * So it runs through the grid's **external** filter hooks, beside the base
 * filter — the path the saved filter already uses precisely because it must not
 * pass through AG Grid's model. `api.onFilterChanged()` re-runs it with source
 * `api`, which `isUserOriginated` treats as the grid's own bookkeeping. Not the
 * quick filter: that one reports `quickFilter` as its source, which reads as a
 * user action and would reach `onAdHocChange` on every keystroke, and it matches
 * each *word* anywhere in the row rather than the text in one of two fields.
 *
 * ## A store, because three things need one answer
 *
 * The header's 🔍, the box in the filter row and the grid's filter pass are
 * three components AG Grid mounts separately, and none of them is the other's
 * parent. Module-level state would be shared by every grid on the page, so each
 * `ScreenerGrid` makes one of these and hands it to the other two through their
 * params — the same seam as `TrackListBinding`'s getter, and for its reason: a
 * value in the params would have to change identity on every keystroke, and a
 * changed param is a rebuilt column.
 */

/** What the search is doing. Replaced, never mutated — `useSyncExternalStore` compares by identity. */
export interface SymbolSearchState {
  /** Whether the filter row is showing. */
  readonly open: boolean;
  /** What is typed. Only ever non-empty while open. */
  readonly text: string;
  /**
   * Bumped whenever the box should take focus — opening it, and asking again
   * while it is already open. A counter rather than a flag, so asking twice
   * focuses twice.
   */
  readonly focus: number;
}

export interface SymbolSearch {
  get(): SymbolSearchState;
  subscribe(listener: () => void): () => void;
  /**
   * The 🔍. Closed opens it; open and empty closes it; open with text keeps it
   * open and puts the cursor back in the box — **while text is in it the row
   * stays**, so the reason rows are missing is always on screen.
   */
  toggle(): void;
  setText(text: string): void;
  /** The ×: empty the box and keep it open, cursor in it. */
  clear(): void;
  /** Escape: the same rule as the 🔍 — clear if there is text, else close. */
  escape(): void;
  /** A different screen or universe: closed and empty, whatever it was. */
  reset(): void;
}

const CLOSED: SymbolSearchState = { open: false, text: "", focus: 0 };

export function createSymbolSearch(): SymbolSearch {
  let state = CLOSED;
  const listeners = new Set<() => void>();

  const set = (next: SymbolSearchState) => {
    if (next.open === state.open && next.text === state.text && next.focus === state.focus) return;
    state = next;
    for (const listener of listeners) listener();
  };

  return {
    get: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    toggle: () => {
      if (!state.open) set({ open: true, text: "", focus: state.focus + 1 });
      else if (!state.text) set({ ...state, open: false });
      else set({ ...state, focus: state.focus + 1 });
    },
    setText: (text) => set({ ...state, open: true, text }),
    clear: () => set({ ...state, text: "", focus: state.focus + 1 }),
    escape: () =>
      state.text ? set({ ...state, text: "", focus: state.focus + 1 }) : set({ ...state, open: false }),
    reset: () => set({ ...CLOSED, focus: state.focus }),
  };
}

/** The two fields a search reads. `name` is read off the row, shown or not. */
export const SEARCH_FIELDS = ["symbol", "name"] as const;

/**
 * Does this row match what is typed?
 *
 * **Contains**, case-insensitive, on the ticker **or** the company name: `app`
 * finds AAPL and Apple Inc., `/spy` finds AAPL/SPY. Read from the row's data
 * rather than from the columns, so a hidden Name column is still searched — the
 * question is "which company", not "which cell".
 *
 * An empty or blank search matches everything.
 */
export function matchesSymbolSearch(row: Readonly<Record<string, unknown>>, text: string): boolean {
  const needle = text.trim().toLowerCase();
  if (!needle) return true;
  return SEARCH_FIELDS.some((field) => {
    const value = row[field];
    return typeof value === "string" && value.toLowerCase().includes(needle);
  });
}
